import { CommunicationType } from "generated-graphql/graphql";
import { useCallback } from "react";
import {
  AgencyInfo,
  CommunicationFormType,
  FireDepartment,
  LeadAgency,
  Lepc,
  PoliceDepartment,
  RecipientFormType,
} from "../types";
import {
  getCommunicationTypeRestrictions,
  getFireDepartmentInfo,
  getLeadAgencyInfo,
  getLepcInfo,
  getPoliceDepartmentInfo,
} from "../utils/agencyInfo";
import {
  DEFAULT_EMAIL_FROM,
  useCommunicationFormContext,
} from "./useCommunicationForm";

interface RecipientUpdates extends Partial<RecipientFormType> {
  recipientName?: string;
  mailRecipientAddressLine1?: string;
  mailRecipientAddressLine2?: string;
  mailRecipientCity?: string;
  mailRecipientState?: string;
  mailRecipientZip?: string;
  emailRecipientAddresses?: string[];
  emailSubject?: string;
  emailFrom?: string;
  emailReplyTo?: string;
}

type RecipientField =
  | `recipients.${number}.recipientName`
  | `recipients.${number}.communicationType`
  | `recipients.${number}.lepc`
  | `recipients.${number}.fireDepartment`
  | `recipients.${number}.policeDepartment`
  | `recipients.${number}.leadAgency`
  | `recipients.${number}.mailRecipientAddressLine1`
  | `recipients.${number}.mailRecipientAddressLine2`
  | `recipients.${number}.mailRecipientCity`
  | `recipients.${number}.mailRecipientState`
  | `recipients.${number}.mailRecipientZip`
  | `recipients.${number}.emailRecipientAddresses`
  | `recipients.${number}.emailSubject`
  | `recipients.${number}.emailReplyTo`
  | `recipients.${number}.emailBodyEnriched`
  | `recipients.${number}.coverLetterDocument`;

export const useRecipientForm = (index: number) => {
  const formContext = useCommunicationFormContext();
  const {
    mode,
    trigger,
    resetField,
    setValue,
    issues: allIssues,
  } = formContext;
  const setValues = useCallback(
    (updates: RecipientUpdates) => {
      Object.entries(updates).forEach(([key, value]) => {
        setValue(`recipients.${index}.${key}` as RecipientField, value);
      });
    },
    [index, setValue]
  );

  const resetValues = useCallback(
    (keys: RecipientField[]) => {
      keys.forEach((key) => {
        resetField(key, { defaultValue: null });
      });
    },
    [resetField]
  );

  const issues = allIssues.filter(
    (issue) => issue.modelId === index.toString()
  );

  // Watch specific fields instead of the entire recipient object
  const recipientName = formContext.watch(`recipients.${index}.recipientName`);
  const recipientType = formContext.watch(`recipients.${index}.recipientType`);

  const lepc = formContext.watch(`recipients.${index}.lepc`);
  const fireDepartment = formContext.watch(
    `recipients.${index}.fireDepartment`
  );
  const policeDepartment = formContext.watch(
    `recipients.${index}.policeDepartment`
  );
  const leadAgency = formContext.watch(`recipients.${index}.leadAgency`);
  const communicationType = formContext.watch(
    `recipients.${index}.communicationType`
  );

  const { isEmailAllowed, isMailAllowed, preferUncertified } =
    getCommunicationTypeRestrictions(recipientType, lepc, fireDepartment);

  const clearMailInfo = useCallback(() => {
    resetValues([
      `recipients.${index}.communicationType`,
      `recipients.${index}.mailRecipientAddressLine1`,
      `recipients.${index}.mailRecipientAddressLine2`,
      `recipients.${index}.mailRecipientCity`,
      `recipients.${index}.mailRecipientState`,
      `recipients.${index}.mailRecipientZip`,
      `recipients.${index}.emailRecipientAddresses`,
      `recipients.${index}.emailSubject`,
      `recipients.${index}.emailReplyTo`,
      `recipients.${index}.emailBodyEnriched`,
      `recipients.${index}.coverLetterDocument`,
    ]);
  }, [resetValues, index]);

  const clearRecipientInfo = useCallback(() => {
    const fieldsToClear: RecipientField[] = [
      `recipients.${index}.recipientName`,
      `recipients.${index}.lepc`,
      `recipients.${index}.fireDepartment`,
      `recipients.${index}.policeDepartment`,
      `recipients.${index}.leadAgency`,
    ];

    clearMailInfo();
    resetValues(fieldsToClear);
    trigger();
  }, [resetValues, trigger, index, clearMailInfo]);

  const onChangeRecipientType = useCallback(() => {
    clearRecipientInfo();
  }, [clearRecipientInfo]);

  const onChooseLepc = useCallback(
    (lepc: Lepc | null) => {
      clearMailInfo();
      const lepcInfo = getLepcInfo(lepc);
      setValues({
        recipientName: lepcInfo?.name ?? "",
      });
    },
    [setValues, clearMailInfo]
  );

  const onChooseFireDepartment = useCallback(
    (fireDepartmentId: FireDepartment) => {
      clearMailInfo();
      const fireDepartmentInfo = getFireDepartmentInfo(fireDepartmentId);
      setValues({
        recipientName: fireDepartmentInfo?.name ?? "",
      });
    },
    [setValues, clearMailInfo]
  );

  const onChoosePoliceDepartment = useCallback(
    (policeDepartmentId: PoliceDepartment) => {
      clearMailInfo();
      const policeDepartmentInfo = getPoliceDepartmentInfo(policeDepartmentId);
      setValues({
        recipientName: policeDepartmentInfo?.name ?? "",
      });
    },
    [setValues, clearMailInfo]
  );

  const onChooseLeadAgency = useCallback(
    (leadAgency: LeadAgency) => {
      clearMailInfo();
      const leadAgencyInfo = getLeadAgencyInfo(leadAgency);
      setValues({
        recipientName: leadAgencyInfo?.name ?? "",
      });
    },
    [setValues, clearMailInfo]
  );

  const onChooseCommunicationType = useCallback(
    (type: CommunicationType) => {
      if (mode === "view") return;

      let agencyInfo: AgencyInfo | null = null;
      // If we have an agency selected, use their email
      if (lepc) {
        agencyInfo = getLepcInfo(lepc);
      } else if (fireDepartment) {
        agencyInfo = getFireDepartmentInfo(fireDepartment);
      } else if (policeDepartment) {
        agencyInfo = getPoliceDepartmentInfo(policeDepartment);
      } else if (leadAgency) {
        agencyInfo = getLeadAgencyInfo(leadAgency);
      }

      if (agencyInfo?.name) {
        setValues({
          recipientName: agencyInfo.name,
        });
      }

      if (type === CommunicationType.Email) {
        // Clear mail fields
        resetValues([
          `recipients.${index}.mailRecipientAddressLine1`,
          `recipients.${index}.mailRecipientAddressLine2`,
          `recipients.${index}.mailRecipientCity`,
          `recipients.${index}.mailRecipientState`,
          `recipients.${index}.mailRecipientZip`,
        ]);

        // Set default email fields
        setValues({
          emailReplyTo: DEFAULT_EMAIL_FROM,
        });

        if (agencyInfo?.email) {
          setValues({
            emailRecipientAddresses: agencyInfo.email
              ? agencyInfo.email.split(",").map((email) => email.trim())
              : [],
          });
        }
      } else if (
        type === CommunicationType.FirstClassMail ||
        type === CommunicationType.CertifiedMail
      ) {
        // Clear email fields
        resetValues([
          `recipients.${index}.emailRecipientAddresses`,
          `recipients.${index}.emailSubject`,
          `recipients.${index}.emailReplyTo`,
          `recipients.${index}.emailBodyEnriched`,
        ]);

        if (agencyInfo?.mailingAddress) {
          setValues({
            mailRecipientAddressLine1: agencyInfo.mailingAddress ?? "",
            mailRecipientAddressLine2: agencyInfo.mailingAddressLine2 ?? "",
            mailRecipientCity: agencyInfo.city ?? "",
            mailRecipientState: agencyInfo.state ?? "",
            mailRecipientZip: agencyInfo.zip ?? "",
          });
        }
      }
    },
    [
      index,
      setValues,
      resetValues,
      mode,
      lepc,
      fireDepartment,
      policeDepartment,
      leadAgency,
      recipientName,
    ]
  );

  return {
    formContext,
    recipientType,
    lepc,
    fireDepartment,
    policeDepartment,
    leadAgency,
    communicationType,
    isEmailAllowed,
    isMailAllowed,
    preferUncertified,
    issues,
    onChangeRecipientType,
    onChooseLepc,
    onChooseFireDepartment,
    onChoosePoliceDepartment,
    onChooseLeadAgency,
    onChooseCommunicationType,
  };
};
